import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import './risk-overview.css';
import { DashboardStore } from '../../stores/DashboardStore';
import { getUseCaseService } from '../../services/UseCaseService';
import { getRiskReportService } from '../../services/RiskPlusReportService';
import { GenerateReportButton } from '../../components/GenerateReportButton';
import { riskColor, riskLabel, RiskPlusTopNav, RiskPlusPagerNav, RiskPlusPagerLink } from './RiskPlusPageShell';
import { toRankIdx } from '../../entities/risk/Risk';
import type { RiskImpactSummary, RiskPlusReport } from '../../entities/report/RiskPlusReport';
import type { Harm } from '../../entities/risk/Harm';
import { UseCaseChat } from '../../entities/useCaseChat/UseCaseChat';
import RiskPlusReportStore from '../../stores/RiskPlusReportStore';
import { RiskReportDetailModal } from '../RiskPlusReport/RiskReportDetailModal';
import { useReportModalParam } from '../../hooks/useReportModalParam';

const RANK_SEVERITY: Record<string, number> = {
  critical: 5,
  high: 4,
  moderate: 3,
  low: 2,
  negligible: 1,
};

function mostSevereImpact(impacts: RiskImpactSummary[]): RiskImpactSummary | null {
  if (impacts.length === 0) return null;
  return impacts.reduce((top, impact) =>
    (RANK_SEVERITY[impact.riskRating] ?? 0) > (RANK_SEVERITY[top.riskRating] ?? 0) ? impact : top,
  );
}

function ReadinessVerdict({
  report,
  reportsPanel,
}: {
  report: RiskPlusReport;
  reportsPanel?: ReactNode;
}) {
  const verdict = report.executiveReadinessVerdict;
  const rank = verdict.overallRiskRank;
  const topImpact = mostSevereImpact(verdict.useCaseImpacts);

  return (
    <div className="rovw-verdict">
      <div className="rovw-verdict-row">
        <div className="rovw-verdict-main">
          <div className="rovw-verdict-eyebrow">Risk Overview · Readiness verdict</div>
          {verdict.overallVerdict && (
            <h3 className="rovw-verdict-text">{verdict.overallVerdict}</h3>
          )}

          <div className="rovw-verdict-rank">
            <span className="rovw-rank-chip">
              <span className="rovw-sev-dot" style={{ background: riskColor(rank) }} />
              <span>{riskLabel(rank)} risk</span>
            </span>
            {topImpact && (
              <span className="rovw-peak-note">
                <span
                  className="rovw-sev-dot"
                  style={{ background: riskColor(topImpact.riskRating) }}
                />
                <span>
                  {riskLabel(topImpact.riskRating)} exposure to {topImpact.impactArea.toLowerCase()}
                </span>
              </span>
            )}
          </div>
        </div>

        {reportsPanel && <div className="rovw-verdict-reports">{reportsPanel}</div>}
      </div>
    </div>
  );
}

function LeadershipActionsCard({ report }: { report: RiskPlusReport }) {
  const interpretation = report.executiveReadinessVerdict.executiveInterpretation;
  if (interpretation.length === 0) return null;

  return (
    <div className="rovw-card rovw-card-tinted">
      <div className="rovw-card-hdr">
        <span className="rovw-card-title">What leadership should do</span>
      </div>
      <div className="rovw-card-pad">
        <ol className="rovw-interp">
          {interpretation.map((point, i) => (
            <li key={i}>
              <span className="rovw-interp-num">{i + 1}</span>
              <span>{point}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function KeyExposureAreasCard({ report }: { report: RiskPlusReport }) {
  const impacts = report.executiveReadinessVerdict.useCaseImpacts;
  if (impacts.length === 0) return null;

  return (
    <div className="rovw-card">
      <div className="rovw-card-hdr">
        <span className="rovw-card-title">Key Exposure Areas</span>
      </div>
      <div>
        {impacts.map((impact, i) => (
          <div key={i} className="rovw-area-row">
            <div className="rovw-area-name">{impact.impactArea}</div>
            <div>
              <span className="rovw-sev-chip">
                <span
                  className="rovw-sev-dot"
                  style={{ background: riskColor(impact.riskRating) }}
                />
                {riskLabel(impact.riskRating)}
              </span>
            </div>
            <div className="rovw-area-desc">{impact.description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RiskConcentrationCard({
  report,
  onOpenDetail,
}: {
  report: RiskPlusReport;
  onOpenDetail: () => void;
}) {
  const summary = report.deploymentProfile.riskConcentration;
  if (!summary) return null;

  return (
    <div className="rovw-card">
      <div className="rovw-card-hdr">
        <span className="rovw-card-title">Risk concentration</span>
      </div>
      <div className="rovw-concentration">
        <span className="aq-icon" aria-hidden>insights</span>
        <div>
          <div className="rovw-conc-body">{summary}</div>
          <button type="button" className="rovw-conc-link" onClick={onOpenDetail}>
            View concentration detail
            <span className="aq-icon" style={{ fontSize: 15 }} aria-hidden>
              arrow_forward
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

type ConcentrationRow = {
  label: string;
  desc: string;
  rank: Harm['harmRank'];
  w: string;
};

function concentrationRows(entries: [string, Harm][]): ConcentrationRow[] {
  return entries
    .map(([label, harm]) => ({
      label,
      desc: harm.description,
      rank: harm.harmRank,
      w: `${(toRankIdx(harm.harmRank) / 5) * 100}%`,
    }))
    .sort((a, b) => toRankIdx(b.rank) - toRankIdx(a.rank));
}

function ConcentrationBar({ row }: { row: ConcentrationRow }) {
  return (
    <div>
      <div className="rovw-cbar-row">
        <div className="rovw-cbar-label">{row.label}</div>
        <div className="rovw-cbar-track">
          <div className="rovw-cbar-fill" style={{ width: row.w, background: riskColor(row.rank) }} />
        </div>
        <span className="rovw-cbar-chip rovw-sev-chip">
          <span className="rovw-sev-dot" style={{ background: riskColor(row.rank) }} />
          {riskLabel(row.rank)}
        </span>
      </div>
      <div className="rovw-cbar-desc">{row.desc}</div>
    </div>
  );
}

function ConcentrationDrawer({ report, onClose }: { report: RiskPlusReport; onClose: () => void }) {
  const { internal, external } = report.deploymentProfile.aiSystemImpact;

  const internalRows = useMemo(
    () =>
      concentrationRows([
        ['Financial', internal.financial],
        ['Reputational', internal.reputational],
        ['Operational', internal.operational],
        ['Legal', internal.legal],
      ]),
    [internal],
  );

  const externalRows = useMemo(
    () =>
      concentrationRows([
        ['People', external.people],
        ['Organisations', external.organisations],
        ['Ecosystems', external.ecosystems],
      ]),
    [external],
  );

  const overallRank = report.executiveReadinessVerdict.overallRiskRank;
  const peak = [...internalRows, ...externalRows].reduce<ConcentrationRow | null>(
    (top, row) => (!top || toRankIdx(row.rank) > toRankIdx(top.rank) ? row : top),
    null,
  );
  const showPeak = !!peak && toRankIdx(peak.rank) > toRankIdx(overallRank);
  const verdictNote =
    showPeak && peak
      ? `${peak.label} exposure is rated ${riskLabel(peak.rank)} while the overall risk rank stays ${riskLabel(overallRank)}. Concentration highlights where harm lands, not the overall verdict.`
      : '';

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="rovw-scrim" onClick={onClose}>
      <div className="rovw-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rovw-drawer-hdr">
          <div className="rovw-drawer-hdr-top">
            <div className="rovw-drawer-hdr-ic">
              <span className="aq-icon" aria-hidden>insights</span>
            </div>
            <div className="rovw-drawer-title">Risk concentration</div>
            <button type="button" className="rovw-drawer-close" onClick={onClose} aria-label="Close">
              <span className="aq-icon" aria-hidden>close</span>
            </button>
          </div>
        </div>
        <div className="rovw-drawer-body">
          <div className="rovw-dsec">
            <div className="rovw-dsec-label">Internal concentration</div>
            <div className="rovw-conc-explain">
              Internal concentration shows where the risk falls on your organisation itself, across
              financial, reputational, operational and legal harm.
            </div>
            {internalRows.map((row, i) => (
              <ConcentrationBar key={i} row={row} />
            ))}
          </div>

          <div className="rovw-dsec">
            <div className="rovw-dsec-label">External concentration</div>
            <div className="rovw-conc-explain">
              External concentration shows who outside your organisation is most exposed, across the
              people who use the system, the organisations affected and the wider ecosystem.
            </div>
            {externalRows.map((row, i) => (
              <ConcentrationBar key={i} row={row} />
            ))}
          </div>

          {verdictNote && (
            <div className="rovw-scope-band">
              <span className="aq-icon" aria-hidden>info</span>
              <div className="rovw-scope-band-text">{verdictNote}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RiskDriversPreconditionsCard({ report }: { report: RiskPlusReport }) {
  const drivers = report.useCaseOverview.riskDrivers;
  const preconditions = report.leadershipSummary.deploymentRequirements;
  const [open, setOpen] = useState(false);

  if (drivers.length === 0 && preconditions.length === 0) return null;

  return (
    <div className="rovw-card">
      <button
        type="button"
        className={`rovw-card-hdr rovw-collapse-hdr${open ? ' rovw-open' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className="rovw-card-title">Risk drivers and preconditions</span>
        <span className="rovw-quiet-note">
          {open ? 'Hide detail' : `Show ${drivers.length} drivers and ${preconditions.length} preconditions`}
        </span>
        <span className="rovw-collapse-chev aq-icon" aria-hidden>
          expand_more
        </span>
      </button>

      {open && (
        <>
          {drivers.length > 0 && (
            <>
              <div className="rovw-collapse-subhdr">Why risk mitigation is required</div>
              <div className="rovw-stack">
                {drivers.map((d, i) => (
                  <div key={i} className="rovw-driver-item">
                    <span className="rovw-driver-ic aq-icon" aria-hidden>
                      chevron_right
                    </span>
                    <div>
                      <div className="rovw-stack-label">{d.label}</div>
                      <div className="rovw-stack-desc">{d.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {preconditions.length > 0 && (
            <>
              <div className="rovw-collapse-subhdr">Minimum preconditions before deployment</div>
              <div className="rovw-precond-note">
                These may be addressed in any order; there is no required sequence.
              </div>
              <div className="rovw-stack">
                {preconditions.map((p, i) => (
                  <div key={i} className="rovw-stack-item">
                    <span className="rovw-interp-num">{i + 1}</span>
                    <div>
                      <div className="rovw-stack-label">{p.label}</div>
                      <div className="rovw-stack-desc">{p.requirement}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function ScopeNotes({ report }: { report: RiskPlusReport }) {
  const notes = report.executiveReadinessVerdict.scopeNotes;
  if (notes.length === 0) return null;

  return (
    <div className="rovw-scope-band">
      <span className="aq-icon" aria-hidden>info</span>
      <div className="rovw-scope-band-text">{notes.join(' ')}</div>
    </div>
  );
}

const RiskPlusUseCaseOverview = () => {
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string }>();
  const navigate = useNavigate();

  const isLoadingBriefs = DashboardStore.useDashboardStore((s) => s.isLoadingUseCaseBriefs);
  const loadUseCaseBriefs = DashboardStore.useDashboardStore((s) => s.loadUseCaseBriefs);
  const useCaseBriefs = DashboardStore.useDashboardStore((s) => s.useCaseBriefs);

  const brief = useMemo(
    () => useCaseBriefs.find((b) => b.useCaseChatId === useCaseChatId),
    [useCaseBriefs, useCaseChatId],
  );

  const [resolvedChat, setResolvedChat] = useState<UseCaseChat | null>(null);

  type ReportItem = { reportType: string; reportTitle: string; reportId: string };
  const [useCaseChatReports, setUseCaseChatReports] = useState<{
    impactPlusReport: ReportItem | null;
    riskPlusReport: ReportItem | null;
  } | null>(null);

  const [riskReport, setRiskReport] = useState<RiskPlusReport | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [showConcDrawer, setShowConcDrawer] = useState(false);
  const riskReportModal = useReportModalParam('riskPlusReportModal');
  const setRiskPlusReportInStore = RiskPlusReportStore.useRiskPlusReportStore((s) => s.setRiskPlusReport);

  useEffect(() => {
    void loadUseCaseBriefs();
  }, [loadUseCaseBriefs]);

  useEffect(() => {
    if (!useCaseChatId) return;
    void getUseCaseService()
      .getUseCaseById(useCaseChatId)
      .then(setResolvedChat)
      .catch(() => setResolvedChat(null));
  }, [useCaseChatId]);

  useEffect(() => {
    if (!useCaseChatId) return;
    void getUseCaseService()
      .getUseCaseChatReports(useCaseChatId)
      .then(setUseCaseChatReports)
      .catch(() => setUseCaseChatReports(null));
  }, [useCaseChatId]);

  const riskPlusReportId = useCaseChatReports?.riskPlusReport?.reportId ?? null;

  const loadRiskReport = useCallback(async () => {
    if (!riskPlusReportId) {
      setRiskReport(null);
      setReportError(null);
      setIsLoadingReport(false);
      return;
    }
    setReportError(null);
    setIsLoadingReport(true);
    try {
      const report = await getRiskReportService().getRiskReportById(riskPlusReportId);
      setRiskReport(report);
    } catch (err) {
      setRiskReport(null);
      setReportError(err instanceof Error ? err.message : 'Could not load Risk+ report.');
    } finally {
      setIsLoadingReport(false);
    }
  }, [riskPlusReportId]);

  useEffect(() => {
    void loadRiskReport();
  }, [loadRiskReport]);

  useEffect(() => {
    if (riskReportModal.isOpen && riskReport) {
      setRiskPlusReportInStore(riskReport);
    }
  }, [riskReportModal.isOpen, riskReport, setRiskPlusReportInStore]);

  const refreshReports = useCallback(() => {
    void getUseCaseService()
      .getUseCaseChatReports(useCaseChatId)
      .then(setUseCaseChatReports)
      .catch(() => setUseCaseChatReports(null));
    void loadUseCaseBriefs();
  }, [useCaseChatId, loadUseCaseBriefs]);

  const isProcessed = brief?.contentStatus === 'processed' || brief?.contentStatus === 'expertValidated';
  const isSufficient = brief?.contentStatus === 'sufficient';
  const showReportsPanel = isProcessed || isSufficient;

  if (!useCaseChatId) return null;

  if (isLoadingBriefs) {
    return (
      <div className="rovw-page min-h-screen p-6">
        <div className="mx-auto max-w-7xl animate-pulse rounded-xl bg-white p-8 shadow-sm">
          <div className="h-4 w-1/3 rounded bg-gray-200" />
          <div className="mt-4 h-8 w-2/3 rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="rovw-page min-h-screen p-6">
        <div className="mx-auto max-w-7xl rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-midnight-navi">Use case not found.</p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
          >
            Back to register
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rovw-page min-h-screen pb-10 pt-6">
      <div className="mx-auto max-w-[1180px] px-4 md:px-6">

        <RiskPlusTopNav
          useCaseChatId={useCaseChatId}
          secondaryNav={
            <RiskPlusPagerNav>
              <RiskPlusPagerLink
                to={`/use-case/${encodeURIComponent(useCaseChatId)}/deployment-risk-profile`}
                label="Risk Domains"
                direction="next"
              />
            </RiskPlusPagerNav>
          }
        />

        {riskPlusReportId && isLoadingReport && !riskReport && (
          <div className="mt-4 animate-pulse space-y-3">
            <div className="h-24 rounded-xl bg-gray-100" />
            <div className="h-20 rounded-xl bg-gray-100" />
            <div className="h-48 rounded-xl bg-gray-100" />
          </div>
        )}

        {riskPlusReportId && !isLoadingReport && reportError && !riskReport && (
          <div
            className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-xs"
            role="alert"
          >
            <div className="font-semibold">Could not load Risk+ report</div>
            <div className="mt-1">{reportError}</div>
            <button
              type="button"
              onClick={() => void loadRiskReport()}
              className="mt-3 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {!riskPlusReportId && !isLoadingReport && showReportsPanel && (
          <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center shadow-xs">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
              <FileText className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-midnight-navi">No Risk+ report generated yet</p>
            <p className="mt-1 text-xs text-gray-500">
              Generate a Risk+ report to see the readiness verdict, risk profile, key risks, and required controls.
            </p>
            {resolvedChat && (
              <div className="mt-4 flex justify-center">
                <GenerateReportButton
                  useCaseChat={resolvedChat}
                  reportType="risk"
                  label="Generate Risk+ Report"
                  disabled={brief.jobIsActive}
                  onGenerateReport={refreshReports}
                />
              </div>
            )}
          </div>
        )}

        {riskReport && (
          <div className="rovw mt-6">
            <ReadinessVerdict
              report={riskReport}
              reportsPanel={
                <>
                  <div className="rovw-verdict-reports-label">Reports</div>
                  <button
                    type="button"
                    className="rovw-verdict-reports-link"
                    onClick={() => riskReportModal.open(riskPlusReportId!)}
                  >
                    <FileText className="h-4 w-4" strokeWidth={2} />
                    Risk+ Report
                  </button>
                </>
              }
            />
            <LeadershipActionsCard report={riskReport} />
            <KeyExposureAreasCard report={riskReport} />
            <RiskConcentrationCard report={riskReport} onOpenDetail={() => setShowConcDrawer(true)} />
            <RiskDriversPreconditionsCard report={riskReport} />
            <ScopeNotes report={riskReport} />
          </div>
        )}
      </div>

      {showConcDrawer && riskReport && (
        <ConcentrationDrawer report={riskReport} onClose={() => setShowConcDrawer(false)} />
      )}

      <RiskReportDetailModal
        isOpen={riskReportModal.isOpen}
        onClose={() => {
          riskReportModal.close();
          setRiskPlusReportInStore(null);
        }}
      />
    </div>
  );
};

export default RiskPlusUseCaseOverview;
